import { getIn } from "formik";
import { withNamespaces } from "components/Form/FormikControls/lib";

export const EXCLUDE_FROM_UPDATE_COUNT = "EXCLUDE_FROM_UPDATE_COUNT";

/**
 * Get the value of fieldName from MLA Supplementary Data (or nested sale lot) if it can
 * be found.
 *
 * For certain fields apply some cryptic logic, potentially returning null, or the magic value
 * EXCLUDE_FROM_UPDATE_COUNT which is used somehow for MLA bulk-updating.
 *
 * TODO: refactor this function, make code SOLID (using the single responsibility principle)
 */
export function resolveValue(
  mlaDataModel: MLASupplementaryData,
  fieldName: string,
  emptyValue: string | number | null,
): string | number | null {
  if (!mlaDataModel) {
    return emptyValue;
  }
  switch (fieldName) {
    case "ageId":
    case "breedId":
    case "estimatedAverageMassGrams":
    case "pricingTypeId":
    case "productId":
    case "sexId":
    case "totalPriceCents":
      return (
        mlaDataModel[fieldName] ||
        getIn(mlaDataModel, withNamespaces(["saleLot"], fieldName))
      );

    case "estCarcassWeight":
    case "frameStructure":
    case "joinedEnd":
    case "joinedStart":
    case "shearing":
    case "shearingStatus":
      return (
        mlaDataModel[fieldName] ||
        getIn(
          mlaDataModel,
          withNamespaces(["saleLot", "draftingAttributes"], fieldName),
        )
      );

    case "muscleScore":
    case "fatScore": {
      // Muscle and fat scores have multiple fields - find the most common.
      const mlaScore = mlaDataModel[fieldName];
      if (mlaScore) {
        // We have an override - that's our value.
        return mlaDataModel[fieldName];
      }
      // Find the most common value underneath - they're stored as, eg,
      // fatScore1: number of animals with a fat score of 1
      // fatScore2 - number of animals with a fat score of 2
      // ...
      const resolvedValue = [1, 2, 3, 4, 5, 6].reduce(
        ([score, highestFieldNumber], fieldNumber) => {
          const saleLotValue = getIn(
            mlaDataModel,
            withNamespaces(
              ["saleLot", "draftingAttributes"],
              `${fieldName}${fieldNumber}`,
            ),
          );
          if (score === null || (saleLotValue && saleLotValue > score)) {
            return [fieldNumber, saleLotValue];
          }
          return [highestFieldNumber, fieldNumber];
        },
        [null, null],
      );
      if (resolvedValue[0]) {
        return resolvedValue[0];
      }
      return null;
    }
    case "dressingRangeId": {
      // Dressing range an underlying check
      // The MLA value and the Sale Lot root entries are an ID referencing a value.
      // the draftingAttributes value is a VALUE.
      // In the feed, if a specific value has been entered for the attribute, that gets sent.
      // So we will NOT be overriding/setting it.
      // Hence - this value does not count toward a value you will be updating!
      // Return the SELECTED value to result in not saying it will be updated.
      const draftingAttributesValue = getIn(
        mlaDataModel,
        withNamespaces(["saleLot", "draftingAttributes"], "dressingPercent"),
      );
      if (draftingAttributesValue) {
        return EXCLUDE_FROM_UPDATE_COUNT;
      }
      return (
        getIn(mlaDataModel, fieldName) ||
        getIn(mlaDataModel, withNamespaces(["saleLot"], fieldName))
      );
    }

    case "estimatedAverageWeightId": {
      // Estimated Live Weight may fall upward to different values
      // It also has an underlying relevance - we may have an ACTUAL live weight!
      // The data feed will send ....
      // The actual live weight *
      // estimated_average_mass_grams *
      // min_mass_grams / max_mass_grams *
      // The MLA data
      // The Sale Lot's estimated weight.
      //
      // In the specific (*) values - this is better data than a broad sweep of MLA overrides
      // so we will not be "overwriting" the data.
      // In the last two instance (MLA data, Sale Lots estimated) we revert to the same considerations as other fields

      // Do any of the * values exist?
      if (
        getIn(mlaDataModel, withNamespaces(["saleLot"], "totalMassGrams")) ||
        getIn(
          mlaDataModel,
          withNamespaces(["saleLot"], "estimatedAverageMassGrams"),
        ) ||
        (getIn(
          mlaDataModel,
          withNamespaces(["saleLot", "draftingAttributes"], "maxMassGrams"),
        ) &&
          getIn(
            mlaDataModel,
            withNamespaces(["saleLot", "draftingAttributes"], "maxMassGrams"),
          ))
      ) {
        return EXCLUDE_FROM_UPDATE_COUNT;
      }
      // Fallback to flat comparing mla to sale lot data.
      return (
        mlaDataModel[fieldName] ||
        getIn(mlaDataModel, withNamespaces(["saleLot"], fieldName))
      );
    }

    default:
  }
  return emptyValue;
}
